import { Button, ComboChart, Flex, Icon, Loader, Text } from 'components/ui';
import { format } from 'date-fns';
import { useListingAnalytics } from 'hooks/use-listing-analytics';
import { ListingRead } from 'lib/api/generated';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { useMedia } from 'react-use';
import { DATE_FORMAT } from 'settings/config';
import styled, { css } from 'styled-components';
import { breakpoints } from '../../styles/breakpoints';
import { globalTheme } from '../../styles/global-theme';

const AnalyticsContainer = styled.div<{ isMobile: boolean }>`
  background-color: ${globalTheme.colors.background};
  padding: ${globalTheme.space[2]};
  border-radius: 16px;‚
  margin-bottom: 24px;
  ${({ isMobile }) =>
    isMobile &&
    css`
      padding: 20px;
    `};
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  gap: 12px;

  @media screen and (max-width: ${breakpoints.large}) {
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  @media screen and (max-width: ${breakpoints.small}) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  @media print {
    margin-bottom: 0;
  }
`;

const StatCard = styled.div<{ $accentColor: string }>`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  background: #ffffff;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  position: relative;
  overflow: hidden;
  transition: box-shadow 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: ${({ $accentColor }) => $accentColor};
    border-radius: 12px 0 0 12px;
  }

  @media screen and (max-width: ${breakpoints.small}) {
    padding: 16px 18px;
    gap: 12px;
  }
  @media print {
    padding: 0;
  }
`;

const StatIconWrapper = styled.div<{ $bgColor: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${({ $bgColor }) => $bgColor};
  flex-shrink: 0;

  @media screen and (max-width: ${breakpoints.small}) {
    width: 40px;
    height: 40px;
    border-radius: 10px;
  }
`;

const StatContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  .stat-label {
    font-size: 14px;
    font-weight: 500;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .stat-value {
    font-size: 32px;
    font-weight: 700;
    color: #1a1a1a;
    line-height: 1.1;

    span {
      font-size: 16px;
      font-weight: 200;
      margin-left: 5px;
    }
  }

  @media screen and (max-width: ${breakpoints.small}) {
    .stat-value {
      font-size: 26px;
    }

    .stat-label {
      font-size: 14px;
    }
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 24px;
`;

interface ListingAnalyticsInfoProps {
  listingDetails: ListingRead;
}

export function ListingAnalyticsInfo({
  listingDetails,
}: ListingAnalyticsInfoProps) {
  const router = useRouter();
  const isMobile = useMedia(`(max-width: ${breakpoints.medium})`, false);

  const { data: analyticsData, isLoading } = useListingAnalytics({
    listingId: listingDetails?.id || '',
  });

  const timeSeriesData = useMemo(() => {
    if (!analyticsData) return [];

    let rawData: any[] = [];

    if (Array.isArray(analyticsData)) {
      if (analyticsData.length > 0 && 'impression_views' in analyticsData[0]) {
        rawData = analyticsData[0].impression_views || [];
      } else {
        rawData = analyticsData;
      }
    } else if (
      typeof analyticsData === 'object' &&
      'impression_views' in analyticsData
    ) {
      rawData = (analyticsData as any).impression_views || [];
    }

    return rawData.map((item: any) => ({
      ...item,
      date: item.date
        ? format(new Date(item.date), DATE_FORMAT.xShort)
        : item.date,
    }));
  }, [analyticsData]);

  const formatNumber = (num: number): string => {
    return num.toLocaleString();
  };

  return (
    <>
      <Text
        as='h3'
        fontSize={4}
        fontWeight='bold'
        style={{
          padding: `${globalTheme.space[0]} 0`,
        }}
      >
        Listing Analytics
      </Text>
      <Text
        style={{
          padding: `0 0 ${globalTheme.space[0]} 0`,
        }}
      >
        Past 14 days
      </Text>
      <AnalyticsContainer isMobile={isMobile}>
        {isLoading ? (
          <Flex
            direction='column'
            justifyContent='center'
            alignItems='center'
            gap={0}
          >
            <Loader />
            <Text textAlign='center'>Loading analytics...</Text>
          </Flex>
        ) : (
          <Flex direction='column' gap={1}>
            <StatsRow>
              <StatCard $accentColor='#3f51b5'>
                <StatIconWrapper $bgColor='rgba(63, 81, 181, 0.1)'>
                  <Icon component='EyeIcon' size='24px' />
                </StatIconWrapper>
                <StatContent>
                  <div className='stat-label'>Impressions</div>
                  <div className='stat-value'>
                    {formatNumber(
                      listingDetails?.metadata?.analytics?.impressions?.total
                    )}
                    <span>
                      (
                      {formatNumber(
                        listingDetails?.metadata.analytics.impressions
                          ?.total_all
                      )}{' '}
                      all time )
                    </span>
                  </div>
                </StatContent>
              </StatCard>

              <StatCard $accentColor='#2196f3'>
                <StatIconWrapper $bgColor='rgba(33, 150, 243, 0.1)'>
                  <Icon component='ClickIcon' size='24px' dontFill />
                </StatIconWrapper>
                <StatContent>
                  <div className='stat-label'>Clicks</div>
                  <div className='stat-value'>
                    {formatNumber(
                      listingDetails?.metadata?.analytics?.views?.total
                    )}
                    <span>
                      (
                      {formatNumber(
                        listingDetails?.metadata.analytics.views?.total_all
                      )}{' '}
                      all time )
                    </span>
                  </div>
                </StatContent>
              </StatCard>
            </StatsRow>

            <ComboChart data={timeSeriesData} height={300} />

            <ButtonWrapper>
              <Button
                size='small'
                variant='outline'
                onClick={() =>
                  router.push(
                    `/marketplace/listing/analytics/${listingDetails?.id}`
                  )
                }
              >
                See Full Report
              </Button>
            </ButtonWrapper>
          </Flex>
        )}
      </AnalyticsContainer>
    </>
  );
}

export default ListingAnalyticsInfo;
